import { Center, Heading, Flex } from '@chakra-ui/layout';
import {
    Button,
    Spinner,
    Table,
    Tbody,
    Td,
    Th,
    Thead,
    Tr,
    useToast,
    useDisclosure,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    Input,
} from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import { useContext, useEffect, useState, useRef } from 'react';
import { FaTrash, FaCheck } from 'react-icons/fa';
import SelectedGameContext from '../context/SelectedGameContext';
import {
    WhitelistUserData,
    WhiteListUsersDataResponse,
    addWhitelistUser,
    removeWhitelistUser,
    getAllWhitelistUsers,
    getWhitelistUsers,
    getWhitelistUsersNextPage,
    isWhitelistEnabledForGame,
    setWhitelistStatus,
} from '../util/whitelist';
import {
    ProfileError,
    getProfileErrorText,
    getProfileErrorHeader,
} from '../context/ProfileContext';
import { CommonScrollList } from './ScrollList';

interface WhitelistTabProps {
    func: string;
    signal: AbortSignal;
}

export const WhitelistTab: React.FC<WhitelistTabProps> = ({ func, signal }) => {
    const selectedGame = useContext(SelectedGameContext);
    const { keycloak } = useKeycloak();
    const [whitelistUsers, setWhitelistUsers] = useState<WhitelistUserData[]>([]);
    const [usersPaging, setUsersPaging] = useState<string | undefined>(undefined);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [whitelistFlag, setWhitelistFlag] = useState<boolean>(false);
    const [isAddingNewUser, setAddingNewUser] = useState(false);
    const {
        isOpen: isRemoveUserOpen,
        onClose: onRemoveUserClose,
        onOpen: onRemoveUserOpen,
    } = useDisclosure();
    const {
        isOpen: isEnableFlagOpen,
        onClose: onEnableFlagClose,
        onOpen: onEnableFlagOpen,
    } = useDisclosure();
    const cancelRef = useRef<HTMLButtonElement>(null);
    const [selectedUser, setSelectedUser] = useState<WhitelistUserData>();
    const [isRemovingUser, setRemovingUser] = useState(false);
    const [isEnablingFlag, setEnablingFlag] = useState(false);
    const [newUserEmail, setNewUserEmail] = useState('');
    const [errors, setErrors] = useState<ProfileError[]>([]);

    const toast = useToast();
    const license = selectedGame?.currLicense?.key;

    const renderError = () => (
        <Center width="100%" height="100%" display="flex" flexDirection="column">
            <Heading as="h3" size="md">
                {errors.map((error) => getProfileErrorHeader(error))}
            </Heading>
            {errors.map((error) => getProfileErrorText(error))}
        </Center>
    );

    useEffect(() => {
        if (!license) {
            return;
        }
        getWhitelistInfo(true);
    }, [license]);

    const getWhitelistInfo = async (reset: boolean) => {
        try {
            setIsLoadingUsers(true);

            let currData = whitelistUsers;
            let nextPage = usersPaging;
            if (reset) {
                setWhitelistUsers([]);
                setUsersPaging(undefined);
                currData = [];
                nextPage = undefined;
            }

            let resp: WhiteListUsersDataResponse;
            if (nextPage) {
                resp = await getWhitelistUsersNextPage(license, func, nextPage, keycloak.token);
            } else {
                // TODO: Replace `getAllWhitelistUsers` with the previous commented out line `getWhitelistUsers`
                //       to restore old pagination functionality when proper searching is restored

                // resp = await getWhitelistUsers(license, func, keycloak.token);
                resp = await getAllWhitelistUsers(license, func, keycloak.token);
            }

            setWhitelistUsers([...currData, ...resp.data]);
            setUsersPaging(resp.page?.next);

            const resp2 = await isWhitelistEnabledForGame(license, keycloak.token);
            setWhitelistFlag(resp2);
        } catch (err: any) {
            return toast({
                title: 'Error',
                description: err.toString(),
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
        } finally {
            setIsLoadingUsers(false);
        }
    };

    if (errors.length > 0) {
        return (
            <>
                <Heading textAlign={'center'} marginBottom={'1em'}>
                    Oh No! Error Detected
                </Heading>
                {renderError()}
            </>
        );
    }

    if (!license) {
        return (
            <Center width="100%" height="100%">
                <Spinner data-cy="loading" />
            </Center>
        );
    }

    return (
        <>
            {/* whitelist enable flag */}
            <Center>
                {whitelistFlag
                    ? `Whitelist is enabled. Only users below are allowed to perform this function.`
                    : `Whitelist is disabled. Any users are allowed to perform this function.`}
            </Center>
            <Center>
                <Button
                    isLoading={isAddingNewUser}
                    onClick={() => {
                        onEnableFlagOpen();
                    }}
                >
                    {`${whitelistFlag ? 'Disable this whitelist' : 'Enable this whitelist'}`}
                </Button>
            </Center>

            <br />
            <br />

            {/* user list */}
            <Flex>
                <Input
                    name="User email"
                    placeholder="New user's email"
                    marginBottom="0.5em"
                    onChange={(e) => {
                        setNewUserEmail(e.target.value);
                    }}
                    value={newUserEmail}
                />
                <Button
                    isLoading={isAddingNewUser}
                    onClick={async () => {
                        try {
                            setAddingNewUser(true);
                            await addWhitelistUser(license, func, newUserEmail, keycloak.token);
                            toast({
                                title: 'Success!',
                                description: `User ${newUserEmail} added successfully`,
                                status: 'success',
                                duration: 9000,
                                isClosable: true,
                            });
                        } catch (err) {
                            return toast({
                                title: 'Error',
                                description:
                                    err instanceof Error ? err.message : `Unexpected error ${err}`,
                                status: 'error',
                                duration: 9000,
                                isClosable: true,
                            });
                        } finally {
                            setAddingNewUser(false);
                            getWhitelistInfo(true);
                        }
                    }}
                >
                    Add New User
                </Button>
            </Flex>
            <CommonScrollList
                loading={isLoadingUsers}
                onLoadMore={() => {
                    getWhitelistInfo(false);
                }}
                hasMore={usersPaging !== undefined && usersPaging !== ''}
                hasError={false}
                className="flex flex-col gap-2"
            >
                <Table variant="simple" data-cy="assets-table" key={`${func}_table`}>
                    <Thead>
                        <Tr key={`${func}_table_header`}>
                            <Th>Email</Th>
                            <Th>Added At</Th>
                            <Th></Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {whitelistUsers.length > 0 ? (
                            whitelistUsers.map((data, i) => {
                                if (!data) return null;
                                return (
                                    <Tr key={`${func}_${i}`}>
                                        <Td>{data.email}</Td>
                                        <Td>{new Date(data.enabledAt * 1000).toISOString()}</Td>
                                        <Td>
                                            <Button
                                                colorScheme="red"
                                                variant="solid"
                                                onClick={() => {
                                                    setSelectedUser(data);
                                                    onRemoveUserOpen();
                                                }}
                                            >
                                                <FaTrash
                                                    style={{
                                                        marginRight: '1em',
                                                    }}
                                                />
                                                Remove
                                            </Button>
                                        </Td>
                                    </Tr>
                                );
                            })
                        ) : (
                            <Center>{`No users in this whitelist.`}</Center>
                        )}
                    </Tbody>
                </Table>
            </CommonScrollList>

            {isLoadingUsers ? (
                <Center width="100%" height="100%">
                    <Spinner data-cy="loading" />
                </Center>
            ) : (
                <></>
            )}

            {/* Alert dialogue to confirm choice to remove user*/}
            <AlertDialog
                isOpen={isRemoveUserOpen}
                leastDestructiveRef={cancelRef}
                onClose={onRemoveUserClose}
                isCentered
                motionPreset="slideInBottom"
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Remove User?
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            Are you sure you want to remove this user from whitelist:{' '}
                            <b>{selectedUser?.email}</b>?
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            {!isRemovingUser ? (
                                <>
                                    <Button ref={cancelRef} onClick={onRemoveUserClose}>
                                        Cancel
                                    </Button>
                                    <Button
                                        colorScheme="red"
                                        onClick={async () => {
                                            try {
                                                setRemovingUser(true);
                                                await removeWhitelistUser(
                                                    license,
                                                    func,
                                                    selectedUser!.userId,
                                                    keycloak.token,
                                                );
                                                return toast({
                                                    title: 'Success',
                                                    description: `User ${
                                                        selectedUser!.email
                                                    } removed.`,
                                                    status: 'success',
                                                    duration: 9000,
                                                    isClosable: true,
                                                });
                                            } catch (err) {
                                                return toast({
                                                    title: 'Error',
                                                    description: `Failed to remove user ${
                                                        selectedUser!.email
                                                    }. Please try again later...`,
                                                    status: 'error',
                                                    duration: 9000,
                                                    isClosable: true,
                                                });
                                            } finally {
                                                setRemovingUser(false);
                                                onRemoveUserClose();
                                                getWhitelistInfo(true);
                                            }
                                        }}
                                        ml={3}
                                    >
                                        <FaTrash
                                            style={{
                                                marginRight: '1em',
                                            }}
                                        />{' '}
                                        Remove
                                    </Button>
                                </>
                            ) : (
                                <Spinner data-cy="loading" />
                            )}
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            {/* Alert dialogue to confirm enable whitelist for this function*/}
            <AlertDialog
                isOpen={isEnableFlagOpen}
                leastDestructiveRef={cancelRef}
                onClose={onEnableFlagClose}
                isCentered
                motionPreset="slideInBottom"
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            {`${whitelistFlag ? 'Disable' : 'Enable'} this whitelist?`}
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            {`Are you sure you want to ${
                                whitelistFlag ? 'disable' : 'enable'
                            } this whitelist?`}
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            {!isEnablingFlag ? (
                                <>
                                    <Button ref={cancelRef} onClick={onEnableFlagClose}>
                                        Cancel
                                    </Button>
                                    <Button
                                        colorScheme="green"
                                        onClick={async () => {
                                            try {
                                                setEnablingFlag(true);
                                                if (whitelistFlag) {
                                                    await setWhitelistStatus(
                                                        false,
                                                        license,
                                                        keycloak.token,
                                                    );
                                                } else {
                                                    await setWhitelistStatus(
                                                        true,
                                                        license,
                                                        keycloak.token,
                                                    );
                                                }
                                                return toast({
                                                    title: 'Success',
                                                    description: `Whitelist is ${
                                                        whitelistFlag ? 'disabled' : 'enabled'
                                                    }`,
                                                    status: 'success',
                                                    duration: 9000,
                                                    isClosable: true,
                                                });
                                            } catch (err) {
                                                return toast({
                                                    title: 'Error',
                                                    description: `Failed to ${
                                                        whitelistFlag ? 'disable' : 'enable'
                                                    } whitelist.`,
                                                    status: 'error',
                                                    duration: 9000,
                                                    isClosable: true,
                                                });
                                            } finally {
                                                setEnablingFlag(false);
                                                onEnableFlagClose();
                                                getWhitelistInfo(true);
                                            }
                                        }}
                                        ml={3}
                                    >
                                        <FaCheck
                                            style={{
                                                marginRight: '1em',
                                            }}
                                        />{' '}
                                        {`${whitelistFlag ? 'Disable' : 'Enable'}`}
                                    </Button>
                                </>
                            ) : (
                                <Spinner data-cy="loading" />
                            )}
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </>
    );
};
